/**
 * The Plans tab's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/membership-credits/plans` (all optional; a default is never written):
 * - `q`: search on the plan name.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends plans in no defined order, so the
 *   default is Plan name ascending.
 */
import type { MembershipPlan } from '@/api/membershipPlans';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';

export const PLANS_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

export type PlanSortKey = 'name' | 'type' | 'audience' | 'term' | 'price' | 'members' | 'status';
export const PLAN_SORT_KEYS: readonly PlanSortKey[] = [
  'name',
  'type',
  'audience',
  'term',
  'price',
  'members',
  'status',
];
export const PLAN_DEFAULT_SORT: SortState<PlanSortKey> = { key: 'name', dir: 'asc' };

/** Display name for a plan (its name/tier, falling back to the id). */
export function planName(plan: MembershipPlan): string {
  return plan.name.length > 0 ? plan.name : plan.id;
}

/** The Type chip label. */
export function planTypeLabel(plan: MembershipPlan): string {
  return plan.price > 0 ? 'Sellable' : 'Comp / manual';
}

/** The Who-can-buy chip label. */
export function planAudienceLabel(plan: MembershipPlan): string {
  return plan.staffOnly ? 'Staff only' : 'Members and staff';
}

/** The lifecycle Status chip label. */
export function planStatusLabel(plan: MembershipPlan): string {
  if (plan.status === 'DRAFT') return 'Draft';
  if (plan.status === 'INACTIVE') return 'Inactive';
  return 'Active';
}

/**
 * What a plan row shows (and sorts by) in each column. A plan with no `impact` shows a
 * dash for Members, so it sorts with the blanks (last, both directions).
 */
export function planSortValue(plan: MembershipPlan, key: PlanSortKey): SortValue {
  switch (key) {
    case 'name':
      return planName(plan);
    case 'type':
      return planTypeLabel(plan);
    case 'audience':
      return planAudienceLabel(plan);
    case 'term':
      return plan.termDays;
    case 'price':
      return plan.price;
    case 'members':
      return plan.impact?.activeMemberships ?? null;
    case 'status':
      return planStatusLabel(plan);
  }
}

/** The Plans search: the plan name. */
export function planMatches(plan: MembershipPlan, query: string): boolean {
  return matchesSearch(query, [planName(plan)]);
}
